import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, eventUpdateRequestsAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Shield,
  Users,
  Settings,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  Crown,
  Ban,
  Clock,
  DollarSign,
  UserCheck,
  UserX,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Infinity,
  Calendar,
  Inbox
} from 'lucide-react';

export const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Settings state
  const [appSettings, setAppSettings] = useState({
    default_monthly_fee: 9.99,
    trial_duration_days: 7,
    annual_discount_percent: 20
  });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    password: '',
    subscription_status: '',
    custom_monthly_fee: '',
    is_admin: false
  });

  const USERS_PER_PAGE = 10;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, settingsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers({ limit: 100 }),
        adminAPI.getSettings()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setAppSettings(settingsRes.data);

      // Non-blocking fetch of pending update-request count for the nav badge
      eventUpdateRequestsAPI.adminList('pending').then(
        (res) => setPendingRequestCount((res.data || []).length),
      ).catch(() => setPendingRequestCount(0));
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have admin access');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const res = await adminAPI.getUsers({ search: searchTerm, limit: 100 });
      setUsers(res.data);
      setCurrentPage(0);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const openEditDialog = (userItem) => {
    setSelectedUser(userItem);
    setEditForm({
      name: userItem.name || '',
      email: userItem.email || '',
      password: '',
      subscription_status: userItem.subscription_status || 'trial',
      custom_monthly_fee: userItem.custom_monthly_fee?.toString() || '',
      is_admin: userItem.is_admin || false
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      const updateData = {
        name: editForm.name,
        email: editForm.email,
        subscription_status: editForm.subscription_status,
        is_admin: editForm.is_admin
      };
      
      if (editForm.password) {
        updateData.password = editForm.password;
      }
      
      if (editForm.custom_monthly_fee) {
        updateData.custom_monthly_fee = parseFloat(editForm.custom_monthly_fee);
      }
      
      await adminAPI.updateUser(selectedUser.id, updateData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSettingsSubmit = async () => {
    setActionLoading(true);
    try {
      await adminAPI.updateSettings(appSettings);
      toast.success('Settings updated successfully');
      setSettingsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmActionHandler = (action, userItem) => {
    setSelectedUser(userItem);
    setConfirmAction(action);
    setConfirmDialogOpen(true);
  };

  const executeConfirmedAction = async () => {
    if (!selectedUser || !confirmAction) return;
    
    setActionLoading(true);
    try {
      switch (confirmAction) {
        case 'reset-trial':
          await adminAPI.resetTrial(selectedUser.id, appSettings.trial_duration_days);
          toast.success(`Trial reset for ${selectedUser.email}`);
          break;
        case 'grant-lifetime':
          await adminAPI.grantLifetime(selectedUser.id);
          toast.success(`Lifetime access granted to ${selectedUser.email}`);
          break;
        case 'revoke':
          await adminAPI.revokeAccess(selectedUser.id);
          toast.success(`Access revoked for ${selectedUser.email}`);
          break;
        case 'activate':
          await adminAPI.activateSubscription(selectedUser.id);
          toast.success(`Subscription activated for ${selectedUser.email}`);
          break;
        case 'delete':
          await adminAPI.deleteUser(selectedUser.id);
          toast.success(`User ${selectedUser.email} deleted`);
          break;
      }
      setConfirmDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      trial: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Trial' },
      active: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Active' },
      lifetime: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Infinity, label: 'Lifetime' },
      expired: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle, label: 'Expired' },
      revoked: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Revoked' }
    };
    const badge = badges[status] || badges.trial;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedUsers = filteredUsers.slice(
    currentPage * USERS_PER_PAGE,
    (currentPage + 1) * USERS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  const annualPrice = (appSettings.default_monthly_fee * 12 * (1 - appSettings.annual_discount_percent / 100)).toFixed(2);

  return (
    <div className="min-h-screen bg-[#FFFFFF] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#1E3A5F] flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E3A5F]">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">Manage users and subscription settings</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/event-requests">
              <Button
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                data-testid="admin-event-requests-link"
              >
                <Inbox className="w-4 h-4 mr-2" />
                Event Update Requests
                {pendingRequestCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full bg-amber-600 text-white text-xs font-bold">
                    {pendingRequestCount}
                  </span>
                )}
              </Button>
            </Link>
            <Button
              onClick={() => setSettingsDialogOpen(true)}
              className="bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white"
              data-testid="admin-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              App Settings
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500">Total Users</span>
            </div>
            <p className="text-2xl font-bold text-[#1E3A5F]">{stats?.total_users || 0}</p>
          </div>
          
          <div className="bg-green-50 rounded-xl border-2 border-green-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <UserCheck className="w-4 h-4 text-green-600" />
              <span className="text-xs text-green-600">Active</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{stats?.active_subscribers || 0}</p>
          </div>
          
          <div className="bg-purple-50 rounded-xl border-2 border-purple-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-4 h-4 text-purple-600" />
              <span className="text-xs text-purple-600">Lifetime</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">{stats?.lifetime_users || 0}</p>
          </div>
          
          <div className="bg-blue-50 rounded-xl border-2 border-blue-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-blue-600">Trial</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{stats?.trial_users || 0}</p>
          </div>
          
          <div className="bg-yellow-50 rounded-xl border-2 border-yellow-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="text-xs text-yellow-600">Expired</span>
            </div>
            <p className="text-2xl font-bold text-yellow-700">{stats?.expired_users || 0}</p>
          </div>
          
          <div className="bg-red-50 rounded-xl border-2 border-red-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <UserX className="w-4 h-4 text-red-600" />
              <span className="text-xs text-red-600">Revoked</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{stats?.revoked_users || 0}</p>
          </div>
          
          <div className="bg-[#1E3A5F]/10 rounded-xl border-2 border-[#1E3A5F]/30 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-[#1E3A5F]" />
              <span className="text-xs text-[#1E3A5F]">Admins</span>
            </div>
            <p className="text-2xl font-bold text-[#1E3A5F]">{stats?.admin_users || 0}</p>
          </div>
        </div>

        {/* Current Pricing Display */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d4a6f] rounded-xl p-6 mb-8 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-bold text-lg mb-1">Current Subscription Pricing</h3>
              <p className="text-white/70 text-sm">These settings apply to all new subscribers</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-white/70 text-xs">Monthly</p>
                <p className="text-2xl font-bold">${appSettings.default_monthly_fee.toFixed(2)}</p>
                <p className="text-white/50 text-xs">USD</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-xs">Annual ({appSettings.annual_discount_percent}% off)</p>
                <p className="text-2xl font-bold">${annualPrice}</p>
                <p className="text-white/50 text-xs">USD</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-xs">Trial Period</p>
                <p className="text-2xl font-bold">{appSettings.trial_duration_days} days</p>
              </div>
            </div>
          </div>
        </div>

        {/* User Search */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="admin-search-input"
              />
            </div>
            <Button onClick={handleSearch} variant="outline" data-testid="admin-search-btn">
              Search
            </Button>
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Trial End</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Custom Fee</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Admin</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((userItem) => (
                  <tr key={userItem.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-[#1E3A5F]">{userItem.name}</p>
                        <p className="text-sm text-gray-500">{userItem.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(userItem.subscription_status)}
                    </td>
                    <td className="py-3 px-4">
                      {userItem.trial_ends_at ? (
                        <span className="text-sm text-gray-600">
                          {new Date(userItem.trial_ends_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {userItem.custom_monthly_fee ? (
                        <span className="text-sm font-medium text-green-600">
                          ${userItem.custom_monthly_fee}/mo
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">Default</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {userItem.is_admin ? (
                        <Shield className="w-4 h-4 text-[#1E3A5F]" />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(userItem)}
                          title="Edit User"
                          data-testid={`edit-user-${userItem.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => confirmActionHandler('reset-trial', userItem)}
                          title="Reset Trial"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => confirmActionHandler('grant-lifetime', userItem)}
                          title="Grant Lifetime Access"
                          className="text-purple-600 hover:text-purple-700"
                        >
                          <Crown className="w-4 h-4" />
                        </Button>
                        {userItem.subscription_status !== 'active' && userItem.subscription_status !== 'lifetime' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => confirmActionHandler('activate', userItem)}
                            title="Activate Subscription"
                            className="text-green-600 hover:text-green-700"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                        {!userItem.is_admin && userItem.subscription_status !== 'revoked' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => confirmActionHandler('revoke', userItem)}
                            title="Revoke Access"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                        {!userItem.is_admin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => confirmActionHandler('delete', userItem)}
                            title="Delete User"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Showing {currentPage * USERS_PER_PAGE + 1} - {Math.min((currentPage + 1) * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and subscription settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-user-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-user-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>New Password (leave blank to keep current)</Label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Enter new password"
                data-testid="edit-user-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Subscription Status</Label>
              <Select
                value={editForm.subscription_status}
                onValueChange={(value) => setEditForm({ ...editForm, subscription_status: value })}
              >
                <SelectTrigger data-testid="edit-user-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="lifetime">Lifetime</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Custom Monthly Fee (for grandfathering)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.custom_monthly_fee}
                  onChange={(e) => setEditForm({ ...editForm, custom_monthly_fee: e.target.value })}
                  placeholder="Leave blank for default"
                  className="pl-8"
                  data-testid="edit-user-custom-fee"
                />
              </div>
              <p className="text-xs text-gray-500">Leave blank to use the default fee (${appSettings.default_monthly_fee}/mo)</p>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="is_admin"
                checked={editForm.is_admin}
                onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
                data-testid="edit-user-admin"
              />
              <Label htmlFor="is_admin" className="flex items-center gap-2 cursor-pointer">
                <Shield className="w-4 h-4 text-[#1E3A5F]" />
                Admin Access
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={actionLoading}
              className="bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white"
              data-testid="save-user-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* App Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>App Settings</DialogTitle>
            <DialogDescription>
              Configure subscription pricing and trial settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Default Monthly Fee</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={appSettings.default_monthly_fee}
                  onChange={(e) => setAppSettings({ ...appSettings, default_monthly_fee: parseFloat(e.target.value) || 0 })}
                  className="pl-8"
                  data-testid="settings-monthly-fee"
                />
              </div>
              <p className="text-xs text-gray-500">This is the price new subscribers will see</p>
            </div>
            
            <div className="space-y-2">
              <Label>Trial Duration (days)</Label>
              <Input
                type="number"
                min="0"
                value={appSettings.trial_duration_days}
                onChange={(e) => setAppSettings({ ...appSettings, trial_duration_days: parseInt(e.target.value) || 0 })}
                data-testid="settings-trial-days"
              />
              <p className="text-xs text-gray-500">How many days new users get for their free trial</p>
            </div>
            
            <div className="space-y-2">
              <Label>Annual Discount (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={appSettings.annual_discount_percent}
                onChange={(e) => setAppSettings({ ...appSettings, annual_discount_percent: parseInt(e.target.value) || 0 })}
                data-testid="settings-annual-discount"
              />
              <p className="text-xs text-gray-500">
                Annual price: ${(appSettings.default_monthly_fee * 12 * (1 - appSettings.annual_discount_percent / 100)).toFixed(2)}/year
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSettingsSubmit}
              disabled={actionLoading}
              className="bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white"
              data-testid="save-settings-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction === 'delete' ? 'Delete User?' : 
               confirmAction === 'revoke' ? 'Revoke Access?' :
               confirmAction === 'reset-trial' ? 'Reset Trial?' :
               confirmAction === 'grant-lifetime' ? 'Grant Lifetime Access?' :
               'Activate Subscription?'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'delete' && `This will permanently delete ${selectedUser?.email}. This action cannot be undone.`}
              {confirmAction === 'revoke' && `This will revoke access for ${selectedUser?.email}. They will no longer be able to use the app.`}
              {confirmAction === 'reset-trial' && `This will give ${selectedUser?.email} a new ${appSettings.trial_duration_days}-day trial period.`}
              {confirmAction === 'grant-lifetime' && `This will give ${selectedUser?.email} unlimited lifetime access to all features.`}
              {confirmAction === 'activate' && `This will activate the subscription for ${selectedUser?.email}.`}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={executeConfirmedAction}
              disabled={actionLoading}
              className={confirmAction === 'delete' || confirmAction === 'revoke' 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white'}
              data-testid="confirm-action-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
